import { useEffect, useState, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './Button';

export type ConfirmTone = 'danger' | 'primary';

export interface ConfirmOptions {
  title: string;
  /** Spell out the blast radius — what is lost, who is affected, whether it can be undone. */
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
}

/* ---------------------------------------------------------------------------
 * Imperative confirm — the in-brand replacement for window.confirm().
 *
 *   const ok = await confirm({ title: 'Remove GitHub?', tone: 'danger',
 *     description: 'Stored credentials are deleted. Reconnecting requires re-entering them.' });
 *   if (!ok) return;
 *
 * A single <ConfirmDialogHost /> mounted in App hears the request and resolves
 * the promise on Confirm or Cancel. Falls back to window.confirm when the host
 * isn't mounted (tests).
 * ------------------------------------------------------------------------- */

type Resolver = (ok: boolean) => void;

interface Pending extends ConfirmOptions {
  id: number;
  resolve: Resolver;
}

let nextId = 1;
let dispatch: ((req: Pending) => void) | null = null;

export function confirm(opts: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    if (!dispatch) {
      resolve(window.confirm(opts.title));
      return;
    }
    dispatch({ id: nextId++, resolve, ...opts });
  });
}

/** Mount once at the app root. */
export function ConfirmDialogHost() {
  const [current, setCurrent] = useState<Pending | null>(null);

  useEffect(() => {
    dispatch = setCurrent;
    return () => {
      dispatch = null;
    };
  }, []);

  useEffect(() => {
    if (!current) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  if (!current) return null;

  const tone = current.tone ?? 'danger';

  function close(ok: boolean) {
    current?.resolve(ok);
    setCurrent(null);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title">
      <div className="absolute inset-0 bg-black/50" onClick={() => close(false)} aria-hidden />
      <div className="relative w-full max-w-md rounded-xl border border-border bg-surface p-5 shadow-pop">
        <div className="flex items-start gap-3">
          <span
            className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
              tone === 'danger' ? 'bg-danger/15 text-danger' : 'bg-primary-muted text-primary'
            }`}
            aria-hidden
          >
            <AlertTriangle className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 id="confirm-title" className="font-semibold text-fg">
              {current.title}
            </h3>
            {current.description && <div className="mt-1 text-sm text-fg-muted">{current.description}</div>}
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" size="sm" autoFocus onClick={() => close(false)}>
            {current.cancelLabel ?? 'Cancel'}
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} size="sm" onClick={() => close(true)}>
            {current.confirmLabel ?? (tone === 'danger' ? 'Delete' : 'Confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}
